import {Injectable, inject} from '@angular/core'
import {WarehouseProfile} from '@models/configAgent'
import {Endpoints} from '@models/endpoints'
import {Order} from '@models/order/order'
import {OrderProduct} from '@models/order/orderproduct'
import {Product} from '@models/product'
import {CombinedStock, Stock, StockLine} from '@models/stocks/stocks'
import {WareHouse} from '@models/stocks/warehouse'
import {EmbeddedStoreService} from '@service/embeddingService/embedded-store.service'
import {UVEApiService} from '@service/uve/api.service'
import {StocksService} from './stocks.service'

/**
 * Variante embebida de `StocksService`.
 *
 * @description
 * Adapta la obtención de existencias al modo embebido:
 *
 * - `getStocks()` se desactiva (no hay pantalla de stocks global en modo embebido).
 * - `getWareHouses()` se construye a partir de los `WarehouseProfile` enviados en el
 *   payload inicial, sin llamar a PouchDB.
 * - `getStocksWarehouse()` hace una llamada HTTP al endpoint `STOCKS` de la UVE API
 *   pidiendo sólo los productos y almacenes necesarios (`skipPaginationTotalCount` para
 *   ahorrar round-trips de conteo en Couch).
 * - `createStockLines()` simplifica la lógica: no cruza pedidos con perfiles porque en
 *   modo embebido todos los pedidos son del contexto actual.
 */
@Injectable({
  providedIn: 'root',
})
export class StocksEmbeddedService extends StocksService {
  private readonly embeddingStoreService: EmbeddedStoreService = inject(EmbeddedStoreService)
  private readonly uveApiService: UVEApiService = inject(UVEApiService)

  override getStocks = async (): Promise<StockLine[]> => []
  override getWareHouses = async (): Promise<WareHouse[]> =>
    this.embeddingStoreService.warehousesProfiles().reduce((acc, warehouseProfile) => {
      return [
        ...acc,
        ...warehouseProfile.warehouses.map(warehouse => ({
          _id: warehouse,
          name: warehouse,
          code: warehouse,
          qt: 0,
          selected: false,
          um: '',
        })),
      ]
    }, [])
  override getStocksWarehouse = async (warehouseCode: string[], products: Array<OrderProduct>): Promise<Stock[]> => {
    const pow = warehouseCode.filter(w => w !== '').length || 50
    const response = (await this.uveApiService.getPromise(
      Endpoints.STOCKS,
      {
        filters: JSON.stringify({
          products: products.map(p => p.idcProducto).join(','),
          almacen: warehouseCode.join(','),
        }),
        columns: 'acumulatedAmount',
        limit: products.length * (pow + 2),
        offset: 0,
        skipPaginationTotalCount: true,
      },

      []
    )) as any

    return response.data?.map(s => ({
      _id: s.stockLineId,
      idcProducto: s.idcProduct,
      amount: s.amount,
      um: s.umc,
      date: s.date,
      lastUpdated: s.date,
      visibility: [],
      warehouse: s.distributorWarehouseCode,
      calulatedAmount: s.acumulatedAmount,
    }))
  }

  protected override createStockLines = async ({
    combinedStocks,
    productMap,
    totalOrders,
    warehouseProfiles,
    batchSize = 100,
  }: {
    combinedStocks: CombinedStock[]
    productMap: Map<number, Product>
    totalOrders: Order[]
    warehouseProfiles: WarehouseProfile[]
    batchSize?: number
  }): Promise<StockLine[]> => {
    try {
      if (combinedStocks.length === 0) {
        return []
      }
      const allUMs = this.umService.allUmsOrder
      const stockLines: StockLine[] = []

      combinedStocks.forEach(stock => {
        const product = productMap.get(stock.idcProducto)
        if (!product || stockLines.some(stockL => stock.idcProducto === stockL.productId)) {
          return
        }
        const fastFilter = `${stock.idcProducto}#${product.codeByManufacturer}#${product.name}`
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ')
          .replace(/\s/g, '#')
          .toLowerCase()

        const orders = totalOrders
        stockLines.push(
          new StockLine({
            stock: {...stock, product},
            orders,
            warehouses: stock.warehouses,
            allUMs,
            fastFilter,
            configErrors: stock.configErrors,
          })
        )
      })

      return stockLines.map(stock => {
        stock.configErrors = !stock.warehouses.every(warehouse => warehouse.um === stock.umc)
        return stock
      })
    } catch (e) {
      console.error(e)
      return []
    }
  }
}
